import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/UserAvatar";
import { FileText, Download, Loader2, Trash2, Reply, ArrowDownUp, Clock, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

import { confirmar } from "@/components/ConfirmDialog";
import { DuvidaAulaIA, NucleoIcone, useIADisponivel } from "@/components/InnovaIAAluna";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
export const mmss = (s) => { s = Math.max(0, Math.floor(s || 0)); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const x = s % 60; const p = (n) => String(n).padStart(2, "0"); return h ? `${h}:${p(m)}:${p(x)}` : `${p(m)}:${p(x)}`; };
const ago = (iso) => { const d = (Date.now() - new Date(iso).getTime()) / 1000; if (d < 60) return "agora"; if (d < 3600) return `há ${Math.floor(d / 60)} min`; if (d < 86400) return `há ${Math.floor(d / 3600)} h`; return new Date(iso).toLocaleDateString("pt-BR"); };
const ROLE = { admin: "Admin", instructor: "Professor(a)" };

/** Abas abaixo da aula: Comentários, Arquivos e Anotações. */
export function LessonTabs({ courseId, moduleId, lesson, counts, onCounts, playerRef, fileLabel }) {
  const [tab, setTab] = useState("comentarios");
  const ia = useIADisponivel();          // aba da In'Nova IA: só para alunas, quando liberada
  const base = `/courses/${courseId}/lessons/${lesson.id}`;
  const TabBtn = ({ k, label, n }) => (
    <button onClick={() => setTab(k)} data-testid={`lesson-tab-${k}`}
      className={`pb-3 -mb-px text-[15px] font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === k ? "border-[#232D4F] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
      {label} ({n})
    </button>
  );
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="flex gap-5 sm:gap-6 px-5 pt-4 border-b border-slate-200 overflow-x-auto">
        <TabBtn k="comentarios" label="Comentários" n={counts.comments} />
        <TabBtn k="arquivos" label="Arquivos" n={lesson.pdfs?.length || 0} />
        <TabBtn k="anotacoes" label="Anotações" n={counts.notes} />
        {ia && (
          <button onClick={() => setTab("ia")} data-testid="lesson-tab-ia"
            className={`pb-3 -mb-px text-[15px] font-semibold border-b-2 whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${tab === "ia" ? "border-[#1E9E2A] text-[#1E9E2A]" : "border-transparent text-[#1E9E2A]/70 hover:text-[#1E9E2A]"}`}>
            <NucleoIcone className="h-4 w-4" /> <span className="sm:hidden">Tirar dúvida</span><span className="hidden sm:inline">Dúvida sobre esta aula</span>
          </button>
        )}
      </div>
      <div className="p-5">
        {tab === "comentarios" && <Comments base={base} onChange={(n) => onCounts({ comments: n })} />}
        {tab === "arquivos" && <Files courseId={courseId} moduleId={moduleId} lesson={lesson} label={fileLabel} />}
        {tab === "anotacoes" && <Notes base={base} playerRef={playerRef} onChange={(n) => onCounts({ notes: n })} />}
        {tab === "ia" && <DuvidaAulaIA aula={{ id: lesson.id, titulo: lesson.title }}
          onAssistir={(s) => { playerRef?.current?.seek(s); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
      </div>
    </div>
  );
}

function Comments({ base, onChange }) {
  const [list, setList] = useState(null);
  const [order, setOrder] = useState("recentes");
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`${base}/comments`, { params: { order } }).then((r) => { setList(r.data); onChange(r.data.length); }).catch(() => setList([])), [base, order]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const post = async (t, parent_id = "") => {
    if (!t.trim()) return;
    setBusy(true);
    try { await api.post(`${base}/comments`, { text: t, parent_id }); setText(""); setReply(""); setReplyTo(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (c) => { if (!(await confirmar("Excluir este comentário?"))) return; try { await api.delete(`${base}/comments/${c.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  const roots = (list || []).filter((c) => !c.parent_id);
  const replies = (id) => (list || []).filter((c) => c.parent_id === id).sort((a, b) => a.created_at.localeCompare(b.created_at));
  const Item = ({ c, isReply }) => (
    <div className={`flex gap-3 ${isReply ? "ml-11 mt-3" : ""}`}>
      <UserAvatar user={c} size={isReply ? 28 : 36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800">{c.name}</span>
          {ROLE[c.role] && <span className="text-[10px] font-bold uppercase text-white bg-[#1E9E2A] rounded px-1.5 py-0.5">{ROLE[c.role]}</span>}
          <span className="text-xs text-slate-400">{ago(c.created_at)}</span>
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-line break-words mt-0.5">{c.text}</p>
        <div className="flex gap-3 mt-1">
          {!isReply && <button onClick={() => { setReplyTo(c.id); setReply(""); }} className="text-xs text-slate-500 hover:text-[#1E9E2A] inline-flex items-center gap-1"><Reply className="h-3 w-3" /> Responder</button>}
          {c.can_delete && <button onClick={() => del(c)} className="text-xs text-slate-400 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Excluir</button>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="lesson-comments">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">{list ? `${list.length} ${list.length === 1 ? "comentário" : "comentários"}` : ""}</span>
        <button onClick={() => setOrder((o) => (o === "recentes" ? "antigos" : "recentes"))} className="text-sm text-slate-600 inline-flex items-center gap-1.5 hover:text-slate-900">
          <ArrowDownUp className="h-4 w-4" /> Ordenar: {order === "recentes" ? "mais recentes" : "mais antigos"}
        </button>
      </div>
      <div className="space-y-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva uma dúvida ou comentário sobre esta aula…" data-testid="comment-input" />
        <div className="flex justify-end"><Button onClick={() => post(text)} disabled={busy || !text.trim()} className="text-white" style={{ background: "#232D4F" }} data-testid="comment-send">Publicar</Button></div>
      </div>
      {list === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : roots.length === 0 ? <p className="text-sm text-slate-400">Nenhum comentário ainda. Seja o primeiro!</p> : (
        <div className="space-y-5">
          {roots.map((c) => (
            <div key={c.id}>
              <Item c={c} />
              {replies(c.id).map((r) => <Item key={r.id} c={r} isReply />)}
              {replyTo === c.id && (
                <div className="ml-11 mt-3 space-y-2">
                  <Textarea rows={2} autoFocus value={reply} onChange={(e) => setReply(e.target.value)} placeholder={`Responder ${c.name}…`} />
                  <div className="flex gap-2 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setReplyTo(null)}>Cancelar</Button>
                    <Button size="sm" onClick={() => post(reply, c.id)} disabled={busy || !reply.trim()} className="text-white" style={{ background: "#1E9E2A" }}>Responder</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Files({ courseId, moduleId, lesson, label }) {
  const [busy, setBusy] = useState(null);
  const pdfs = lesson.pdfs || [];
  const download = async (i, name) => {
    setBusy(i);
    try {
      const res = await api.get(`/materials/${courseId}/${moduleId}/${lesson.id}/${i}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = name || "material.pdf"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Não foi possível baixar o material."); } finally { setBusy(null); }
  };
  if (!pdfs.length) return <p className="text-sm text-slate-400">Nenhum arquivo nesta aula.</p>;
  return (
    <div className="space-y-2" data-testid="lesson-files">
      <p className="text-xs text-slate-400">{label}. PDFs são gerados com sua identificação (nome, e-mail e data) em todas as páginas.</p>
      {pdfs.map((p, i) => (!/\.pdf($|\?)/i.test(p.url || "") ? (
        <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 hover:border-[#E65100] hover:bg-orange-50/40 group">
          <FileText className="h-5 w-5 text-[#E65100]" /><span className="flex-1 text-sm font-medium text-slate-700 truncate">{p.name}</span>
          <span className="text-xs text-slate-400 group-hover:text-[#E65100] inline-flex items-center gap-1"><Download className="h-4 w-4" /> Abrir</span>
        </a>
      ) : (
        <button key={i} onClick={() => download(i, p.name)} disabled={busy === i} data-testid="btn-download-material-button"
          className="w-full flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 hover:border-[#E65100] hover:bg-orange-50/40 group text-left disabled:opacity-60">
          <FileText className="h-5 w-5 text-[#E65100]" /><span className="flex-1 text-sm font-medium text-slate-700 truncate">{p.name}</span>
          {busy === i ? <Loader2 className="h-4 w-4 animate-spin text-[#E65100]" /> : <span className="text-xs text-slate-400 group-hover:text-[#E65100] inline-flex items-center gap-1"><Download className="h-4 w-4" /> Baixar</span>}
        </button>
      )))}
    </div>
  );
}

function Notes({ base, playerRef, onChange }) {
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`${base}/notes`).then((r) => { setList(r.data); onChange(r.data.length); }).catch(() => setList([])), [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!text.trim()) return;
    const t = playerRef.current ? await playerRef.current.currentTime() : 0;
    try { await api.post(`${base}/notes`, { text, video_time: t }); setText(""); load(); toast.success(`Anotação salva no minuto ${mmss(t)}.`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => { try { await api.put(`${base}/notes/${edit.id}`, { text: edit.text }); setEdit(null); load(); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (n) => { if (!(await confirmar("Excluir esta anotação?"))) return; await api.delete(`${base}/notes/${n.id}`); load(); };
  const go = async (n) => { if (!(await playerRef.current?.seek(n.video_time))) toast.info(`Vá até ${mmss(n.video_time)} no vídeo.`); };
  return (
    <div className="space-y-4" data-testid="lesson-notes">
      <p className="text-xs text-slate-400">Suas anotações são particulares. Cada uma guarda o minuto do vídeo — clique no horário para voltar ao ponto.</p>
      <div className="space-y-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva uma anotação sobre este momento da aula…" data-testid="note-input" />
        <div className="flex justify-end"><Button onClick={add} disabled={!text.trim()} className="text-white" style={{ background: "#1E9E2A" }} data-testid="note-save"><Clock className="h-4 w-4 mr-1.5" /> Salvar no minuto atual</Button></div>
      </div>
      {list === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : list.length === 0 ? <p className="text-sm text-slate-400">Nenhuma anotação ainda.</p> : (
        <div className="space-y-2">
          {list.map((n) => (
            <div key={n.id} className="flex gap-3 rounded-lg border border-slate-200 p-3">
              <button onClick={() => go(n)} className="shrink-0 h-fit font-mono text-xs font-bold text-white bg-[#232D4F] rounded px-2 py-1 hover:bg-[#1E9E2A]" title="Ir para este minuto">{mmss(n.video_time)}</button>
              {edit?.id === n.id ? (
                <div className="flex-1 space-y-2">
                  <Textarea rows={2} value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEdit(null)}><X className="h-4 w-4" /></Button>
                    <Button size="sm" onClick={save} className="text-white" style={{ background: "#1E9E2A" }}><Check className="h-4 w-4" /></Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="flex-1 text-sm text-slate-700 whitespace-pre-line break-words">{n.text}</p>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => setEdit({ id: n.id, text: n.text })} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(n)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
